import 'core-js/modules/es.array.iterator.js';
import 'core-js/modules/web.dom-collections.iterator.js';
import 'core-js/modules/es.string.includes.js';
import 'core-js/modules/es.object.assign.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { styled, Stack, shape, palette, withUtility, css, Text, Snackbar } from '@hub-la/design-system';
import { UploadStatus, AssetUploader, AssetType } from '@hub-la/fe-asset';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import { useFormikContext } from 'formik';
import { isNil, get } from 'lodash';

let _ = t => t,
  _t,
  _t2,
  _t3,
  _t4,
  _t5,
  _t6,
  _t7,
  _t8;
const Icon = styled(Stack)(_t || (_t = _`
  width: 56px;
  height: 56px;
  justify-content: center;
  align-items: center;
  border-radius: ${0};
  background-color: ${0};
`), shape('extraLarge'), palette('surfaceAt2'));
const Card = styled(Stack, {
  shouldForwardProp: propName => propName !== 'hasImage'
})(_t2 || (_t2 = _`
  width: 100%;
  overflow: hidden;
  margin: 0 auto;
  justify-content: center;
  align-items: center;
  background-color: ${0};
  border-radius: ${0};
  display: flex;
  flex-direction: column;
  position: relative;

  ${0}

  ${0}
`), palette('background'), shape('medium'), withUtility(({}, {
  field
}) => {
  if (field === 'mainMedia') {
    return css(_t3 || (_t3 = _`
        height: 338px;
      `));
  }
  return css(_t4 || (_t4 = _`
      height: 191px;
      max-width: 128px;
    `));
}), withUtility(({
  palette
}, {
  hasImage
}) => {
  if (hasImage) {
    return;
  }
  return css(_t5 || (_t5 = _`
      &:hover {
        background-color: ${0};
      }
      border: 1px dashed ${0};
    `), palette('surfaceAt1'), palette('outline'));
}));
const Image = styled('img')(_t6 || (_t6 = _`
  object-fit: cover;
  width: 100%;
  height: 100%;
`));
const DeleteUploadBox = styled(Stack)(_t7 || (_t7 = _`
  width: 36px;
  height: 36px;
  border-radius: ${0};
  background-color: ${0};
  position: absolute;
  top: 10px;
  right: 10px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`), shape('small'), palette('onPrimary'));
const ProgressText = styled(Text)(_t8 || (_t8 = _`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`));
const CardUploader = ({
  fieldName,
  postType,
  hasLoading: _hasLoading = false,
  showSnackbar: _showSnackbar = true
}) => {
  const {
    values,
    setFieldValue
  } = useFormikContext();
  const statusFields = {
    customCover: 'customCoverStatus',
    mainMedia: 'mainMediaProgressStatus'
  };
  const progressFields = {
    customCover: 'customCoverProgress',
    mainMedia: 'mainMediaProgress'
  };
  const statusFieldName = statusFields[fieldName];
  const progressFieldName = progressFields[fieldName];
  const statusField = values[statusFieldName];
  const progressField = values[progressFieldName];
  const field = values[fieldName];
  const handleDelete = () => {
    if (isNil(values[fieldName])) {
      return;
    }
    setFieldValue(fieldName, null);
    setFieldValue(statusFieldName, UploadStatus.NO_CALLED);
    setFieldValue(progressFieldName, 0);
  };
  const getImage = () => {
    if (fieldName === 'customCover') {
      return get(field, 'url', null);
    }
    const fieldType = get(field, 'type');
    if ([AssetType.video, AssetType.application].includes(fieldType)) {
      return get(field, 'metadata.thumbnailUrl');
    }
    if (fieldType === AssetType.image) {
      return get(field, 'url');
    }
  };
  const image = getImage();
  const getTitle = () => {
    if (fieldName === 'customCover') {
      return 'Adicionar capa';
    }
    switch (postType) {
      case 'event':
        return 'Adicione a gravação';
      case 'ebook':
        return 'Adicione um PDF';
      case 'video':
        return 'Adicione um vídeo';
      default:
        return 'Adicione uma imagem ou vídeo';
    }
  };
  const getSubtitle = () => {
    if (fieldName === 'customCover') {
      return null;
    }
    switch (postType) {
      case 'event':
        return 'Você pode adicionar o vídeo depois. (.mp4, .avi, .mpeg...)';
      case 'ebook':
        return 'Formato .pdf, .xls, .doc...';
      case 'video':
        return 'Formato .mp4, .avi, .mpeg...';
      default:
        return 'Formatos .png, .jpg, .pdf, .mp4...';
    }
  };
  const subtitle = getSubtitle();
  const icon = fieldName === 'customCover' ? jsx(ImageOutlinedIcon, {}) : jsx(UploadFileOutlinedIcon, {});
  const isLoading = statusField === UploadStatus.LOADING && _hasLoading;
  const isProcessing = statusField === UploadStatus.PROCESSING && _hasLoading;
  const isOpen = [UploadStatus.ERROR, UploadStatus.SUCCESS, UploadStatus.FILE_NOT_ALLOWED].includes(statusField) && _showSnackbar;
  const variants = {
    [UploadStatus.FILE_NOT_ALLOWED]: 'negative',
    [UploadStatus.ERROR]: 'negative',
    [UploadStatus.SUCCESS]: 'positive'
  };
  const messages = {
    [UploadStatus.ERROR]: 'Upload falhou',
    [UploadStatus.SUCCESS]: 'Upload feito com sucesso',
    [UploadStatus.FILE_NOT_ALLOWED]: 'Formato de arquivo não permitido'
  };
  const variant = variants[statusField];
  const message = messages[statusField];
  return jsxs(Fragment, {
    children: [_showSnackbar && jsx(Snackbar, Object.assign({
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'center'
      },
      open: isOpen,
      onClose: () => {
        setFieldValue(statusFieldName, statusField === UploadStatus.ERROR ? UploadStatus.NO_CALLED : UploadStatus.LOADED);
      },
      closeable: true,
      variant: variant
    }, {
      children: message
    })), isLoading ? jsxs(Card, Object.assign({
      hasImage: false,
      field: fieldName,
      "data-testid": "loading"
    }, {
      children: [jsxs(Stack, Object.assign({
        position: "relative",
        justifyContent: "center",
        alignItems: "center"
      }, {
        children: [jsx(CircularProgress, {
          value: progressField
        }), jsxs(ProgressText, Object.assign({
          variant: "caption"
        }, {
          children: [Math.round(progressField), "%"]
        }))]
      })), jsx(Text, Object.assign({
        variant: "body2",
        marginTop: 1
      }, {
        children: "Fazendo upload..."
      }))]
    })) : isProcessing ? jsx(Card, Object.assign({
      hasImage: false,
      field: fieldName,
      "data-testid": "processing"
    }, {
      children: jsxs(Stack, Object.assign({
        justifyContent: "center",
        alignItems: "center"
      }, {
        children: [jsx(CircularProgress, {}), jsx(Text, Object.assign({
          variant: "body1",
          marginTop: 3
        }, {
          children: "Gerando capa do conte\u00FAdo..."
        })), jsx(Text, Object.assign({
          variant: "body2"
        }, {
          children: "Isso pode levar alguns minutos, por\u00E9m voc\u00EA j\u00E1 pode salvar seu post."
        }))]
      }))
    })) : image ? jsxs(Card, Object.assign({
      hasImage: true,
      field: fieldName
    }, {
      children: [jsx(Image, {
        src: image,
        "data-testid": "image",
        alt: ""
      }), jsx(DeleteUploadBox, Object.assign({
        onClick: handleDelete
      }, {
        children: jsx(DeleteOutlineOutlinedIcon, {})
      }))]
    })) : jsx(AssetUploader, Object.assign({
      field: fieldName,
      statusFieldName: statusFieldName
    }, {
      children: jsxs(Card, Object.assign({
        hasImage: false,
        field: fieldName,
        "data-testid": "upload"
      }, {
        children: [jsx(Icon, {
          children: icon
        }), jsx(Text, Object.assign({
          textAlign: "center",
          marginTop: 2
        }, {
          children: getTitle()
        })), subtitle && jsx(Text, Object.assign({
          "data-testid": "subtitle"
        }, {
          children: subtitle
        }))]
      }))
    }))]
  });
};

export { CardUploader };
